import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePasswordResetTokenValidation, useResetPassword } from '@/hooks/useAuth';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

const createPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/\d/, 'Password must contain at least one number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type CreatePasswordFormValues = z.infer<typeof createPasswordSchema>;

export const Route = createFileRoute('/(public)/create-password/$token')({
  component: CreatePasswordRoute,
});

function CreatePasswordRoute() {
  const { token } = Route.useParams();

  return <CreatePasswordPage token={token} />;
}

export function CreatePasswordPage({ token }: { token: string }) {
  const navigate = useNavigate();
  const tokenValidation = usePasswordResetTokenValidation(token);
  const resetPasswordMutation = useResetPassword();

  const form = useForm<CreatePasswordFormValues>({
    resolver: zodResolver(createPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  async function onSubmit(values: CreatePasswordFormValues) {
    form.clearErrors('root');
    try {
      await resetPasswordMutation.mutateAsync({ token, password: values.password });
      // useResetPassword already calls clearSession() on success — passing
      // `?from=reset` lets the login route's beforeLoad scrub again as a
      // belt-and-suspenders defence (e.g. for the next user signing in on a
      // shared browser).
      toast.success('Password set successfully. Please sign in.');
      navigate({ to: '/login', search: { from: 'reset' } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      form.setError('root', { message });
    }
  }

  if (tokenValidation.isPending) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-sm">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" aria-label="Checking password link" />
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }

  if (tokenValidation.isError) {
    const isInvalidToken = tokenValidation.error.status === 400;

    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">
              {isInvalidToken ? 'Expired Link' : 'Unable to verify link'}
            </CardTitle>
            <CardDescription>
              {isInvalidToken
                ? 'This password link is invalid or has expired.'
                : 'We could not verify this password link. Please try again.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isInvalidToken ? (
              <Button asChild className="w-full">
                <Link to="/login" search={{ from: 'reset' }}>
                  Back to login
                </Link>
              </Button>
            ) : (
              <Button
                type="button"
                className="w-full"
                onClick={() => void tokenValidation.refetch()}
                disabled={tokenValidation.isFetching}
              >
                {tokenValidation.isFetching ? 'Checking link…' : 'Try again'}
              </Button>
            )}
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }

  if (tokenValidation.data?.kind === 'invite') {
    return <Navigate to="/invite/$token" params={{ token }} replace />;
  }

  return (
    <PublicCenteredPage>
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Create password</CardTitle>
          <CardDescription>Choose a password for your account</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root && (
                <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
              )}

              <Button type="submit" className="w-full" disabled={resetPasswordMutation.isPending}>
                {resetPasswordMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting password…
                  </>
                ) : (
                  'Set password'
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
